import axios from 'axios';

// ---------------------------------------------------------------------------
// Developer-only API-call counter (GUNDI-5464)
//
// Gated by a runtime localStorage flag, NOT a build-time env var, so the code
// ships in every bundle (prod included) but stays dormant unless a developer
// opts in on their own browser. Regular users never see any output.
//   Enable:  localStorage.setItem("debug_api_count", "true")
//   Disable: localStorage.removeItem("debug_api_count")
//
// Logging is window-based to guarantee ONE summary per page load. The first
// request of a view opens a fixed collection window (LOG_WINDOW_MS); every
// request fired during that window is tallied. When the window closes we log a
// single cumulative summary and mark the view as logged, so late-arriving
// requests never produce a second line. The tally resets on route change (via
// resetApiCallLog from PageViewTracker) and on a full reload (fresh module
// state), so a plain refresh works too.
// ---------------------------------------------------------------------------

export const API_COUNT_FLAG = 'debug_api_count';
// Wait long enough that all the requests a page chains together have fired.
const LOG_WINDOW_MS = 10000;

export function isApiCountEnabled(): boolean {
  try {
    return localStorage.getItem(API_COUNT_FLAG) === 'true';
  } catch {
    return false;
  }
}

// Per-endpoint tally: how many calls and total server response time (ms).
type EndpointStat = { count: number; totalMs: number };
const apiCallLog: { count: number; byEndpoint: Record<string, EndpointStat> } =
  {
    count: 0,
    byEndpoint: {},
  };
let windowTimer: ReturnType<typeof setTimeout> | null = null;
let windowLogged = false;
let burstStart = 0;

// Reset the tally for a new view. Called on route change.
export function resetApiCallLog(): void {
  if (windowTimer) {
    clearTimeout(windowTimer);
    windowTimer = null;
  }
  windowLogged = false;
  burstStart = 0;
  apiCallLog.count = 0;
  apiCallLog.byEndpoint = {};
}

// Collapse volatile path segments (UUIDs and purely numeric ids) to ":id" and
// drop the query string so e.g. /v2/connections/abc and /v2/connections/def
// share a key. Non-numeric, non-UUID segments are left as-is.
export function normalizeEndpoint(url?: string): string {
  if (!url) return '(unknown)';
  const path = url.split('?')[0];
  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      const isUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment,
        );
      const isNumeric = /^\d+$/.test(segment);
      return isUuid || isNumeric ? ':id' : segment;
    })
    .join('/');
}

function logApiCallSummary(): void {
  windowTimer = null;
  windowLogged = true;
  // Re-check the flag at log time: it may have been turned off mid-window.
  if (!isApiCountEnabled()) return;
  if (apiCallLog.count === 0) return;
  const elapsedMs = Date.now() - burstStart;
  const path =
    typeof window !== 'undefined' ? window.location.pathname : '(unknown)';
  // Build a readable breakdown: calls + total/avg server response time.
  const breakdown: Record<string, string> = {};
  Object.keys(apiCallLog.byEndpoint)
    .sort()
    .forEach((key) => {
      const { count, totalMs } = apiCallLog.byEndpoint[key];
      const avg = count > 0 ? Math.round(totalMs / count) : 0;
      breakdown[key] =
        `${count} call${count === 1 ? '' : 's'}, ${totalMs}ms total (${avg}ms avg)`;
    });
  // eslint-disable-next-line no-console
  console.info(
    `[api] ${path} — ${apiCallLog.count} calls in ${elapsedMs}ms`,
    breakdown,
  );
}

// Called from the request interceptor for every request fired.
function recordApiCall(url?: string): void {
  if (!isApiCountEnabled()) return;
  if (windowLogged) return; // window already closed for this view
  if (apiCallLog.count === 0) {
    burstStart = Date.now();
    windowTimer = setTimeout(logApiCallSummary, LOG_WINDOW_MS);
  }
  apiCallLog.count += 1;
  const key = normalizeEndpoint(url);
  if (!apiCallLog.byEndpoint[key]) {
    apiCallLog.byEndpoint[key] = { count: 0, totalMs: 0 };
  }
  apiCallLog.byEndpoint[key].count += 1;
}

// Called from the response interceptor when a request settles. Adds the server
// response time to the matching endpoint's tally. No-op once the window closed.
function recordApiTiming(url: string | undefined, ms: number): void {
  if (!isApiCountEnabled() || windowLogged) return;
  const key = normalizeEndpoint(url);
  if (!apiCallLog.byEndpoint[key]) return; // request wasn't counted
  apiCallLog.byEndpoint[key].totalMs += ms;
}

export async function createAxiosInstance(token: string) {
  let bearerToken: string;
  if (token) {
    bearerToken = 'bearer ' + token;
  } else {
    bearerToken = '';
  }
  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL_LOCAL,
    validateStatus: function (status) {
      // Only treat 2xx as success - we'll check for redirects in the interceptor
      return status >= 200 && status < 300;
    },
  });

  axiosInstance.defaults.headers.common['Authorization'] = bearerToken;
  axiosInstance.defaults.headers.common['Content-Type'] =
    'application/json;charset=UTF-8';
  axiosInstance.defaults.headers.common['accept'] =
    'application/json, text/plain, */*';

  // Request interceptor to track the original request URL
  axiosInstance.interceptors.request.use(
    function (config) {
      // Store the original request URL to detect redirects later
      (config as any).__originalUrl = config.url;
      (config as any).__originalBaseURL = config.baseURL;
      // Developer-only API-call counter (GUNDI-5464); fully dormant unless the
      // flag is set — no per-request bookkeeping for normal users.
      if (isApiCountEnabled()) {
        (config as any).__apiStart = Date.now();
        recordApiCall(config.url);
      }
      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  // Response interceptor to detect redirects
  axiosInstance.interceptors.response.use(
    function (response) {
      // Developer-only API-call counter (GUNDI-5464); no-op unless flag is set
      const apiStart = (response.config as any).__apiStart;
      if (apiStart)
        recordApiTiming(response.config?.url, Date.now() - apiStart);
      // Check if we were redirected to Keycloak
      const originalUrl = (response.config as any).__originalUrl;
      const originalBaseURL =
        (response.config as any).__originalBaseURL ||
        process.env.REACT_APP_BASE_URL_LOCAL;
      const requestUrl = originalBaseURL
        ? `${originalBaseURL}${originalUrl}`
        : originalUrl;
      const responseUrl = response.request?.responseURL || response.config?.url;

      // Check if response URL is from Keycloak (different domain) or if response is HTML instead of JSON
      const contentType = response.headers['content-type'] || '';
      const isKeycloakRedirect =
        responseUrl && responseUrl.includes('/auth/realms/');

      // Also check if response data looks like HTML (Keycloak login page)
      const responseData = response.data;
      const isHtmlResponse =
        typeof responseData === 'string' &&
        (responseData.includes('<!DOCTYPE html') ||
          responseData.includes('<html') ||
          responseData.includes('keycloak') ||
          responseData.includes('Sign in'));

      if (
        isKeycloakRedirect ||
        isHtmlResponse ||
        (responseUrl &&
          requestUrl &&
          responseUrl !== requestUrl &&
          !responseUrl.startsWith(originalBaseURL || ''))
      ) {
        // We were redirected to Keycloak - session expired
        console.warn('Detected redirect to Keycloak - session expired', {
          requestUrl,
          responseUrl,
          contentType,
          isHtmlResponse,
        });
        localStorage.removeItem('userAuthorized');
        // Set flag to trigger session expired modal
        localStorage.setItem('api_401_detected', 'true');
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
        return Promise.reject({
          response: { status: 401, data: { error: 'Session expired' } },
          message: 'Session expired - redirected to auth server',
        });
      }

      if (response.status === 200)
        localStorage.setItem('userAuthorized', true.toString());
      return response;
    },
    function (error) {
      // Developer-only API-call counter (GUNDI-5464); no-op unless flag is set
      const apiStart = error.config?.__apiStart;
      if (apiStart) recordApiTiming(error.config?.url, Date.now() - apiStart);
      // Handle network errors (CORS, redirects, etc.)
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // This often happens when API redirects to Keycloak (CORS issue)
        // or when browser blocks the redirect due to CORS
        console.warn(
          'Network error - likely due to expired session or CORS redirect:',
          error.message,
        );
        localStorage.removeItem('userAuthorized');
        // Set flag to trigger session expired modal
        localStorage.setItem('api_401_detected', 'true');
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
        // Reject with a 401-like error so components can handle it
        return Promise.reject({
          response: { status: 401, data: { error: 'Session expired' } },
          message: 'Network error - session likely expired',
        });
      }

      // Check if error response is from Keycloak
      const responseUrl =
        error.response?.request?.responseURL || error.config?.url;
      const isKeycloakError =
        responseUrl && responseUrl.includes('/auth/realms/');

      if (isKeycloakError) {
        console.warn('Error response from Keycloak - session expired');
        localStorage.removeItem('userAuthorized');
        // Set flag to trigger session expired modal
        localStorage.setItem('api_401_detected', 'true');
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
        return Promise.reject({
          response: { status: 401, data: { error: 'Session expired' } },
          message: 'Session expired - redirected to auth server',
        });
      }

      // Handle redirect errors (3xx status codes)
      if (
        error.response &&
        error.response.status >= 300 &&
        error.response.status < 400
      ) {
        console.warn('API returned redirect status - session expired');
        localStorage.removeItem('userAuthorized');
        // Set flag to trigger session expired modal
        localStorage.setItem('api_401_detected', 'true');
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
        // Convert to 401 error
        return Promise.reject({
          response: { status: 401, data: { error: 'Session expired' } },
          message: 'Session expired - redirect to auth server',
        });
      }

      if (error.response && error.response.status === 401) {
        // Handle unauthorized error
        console.error('Unauthorized error:', error);
        localStorage.removeItem('userAuthorized');
        // Set flag to trigger session expired modal
        localStorage.setItem('api_401_detected', 'true');
        // Dispatch a custom event to notify components
        window.dispatchEvent(new CustomEvent('api401'));
      }
      return Promise.reject(error);
    },
  );
  return axiosInstance;
}

export async function handleResponse(response: any) {
  if (response.ok) {
    localStorage.setItem('userAuthorized', true.toString());
    return response.json();
  }
  if (response.status === 400) {
    // So, a server-side validation error occurred.
    // Server side validation returns a string error message, so parse as text instead of json.
    const errorText = await response.text();
    throw new Error(errorText);
  }
  if (response.status === 401) {
    // Handle unauthorized error
    console.error('Unauthorized error:', response);
    localStorage.removeItem('userAuthorized');
    // Optionally, you can trigger a logout or refresh token process here
  }
  throw new Error('Network response was not ok.');
}

/**
 * Converts a paginated `next`/`previous` link into a baseURL-relative path.
 *
 * DRF returns these as absolute URLs. Handing an absolute URL back to an axios
 * instance bypasses its baseURL, which breaks the redirect detection in the
 * response interceptor above: it reconstructs the request as
 * `baseURL + config.url`, so an absolute `config.url` yields a doubled URL that
 * never matches `responseURL`. Relativizing keeps every page on the same code
 * path as the first request.
 *
 * A link pointing somewhere other than our own API is not followed at all —
 * returns null so the caller stops paginating rather than fetching an
 * arbitrary host on the API's instruction.
 */
export function toRelativeApiUrl(
  link: string | null | undefined,
): string | null {
  if (!link) return null;
  // Already relative — nothing to do. Scheme-relative links (`//host/...`) are
  // absolute URLs in disguise (axios follows them off-origin), so they take the
  // origin-check path below rather than passing through here.
  if (!/^(https?:)?\/\//i.test(link)) return link;

  const baseURL = process.env.REACT_APP_BASE_URL_LOCAL;
  try {
    const parsed = new URL(link.startsWith('//') ? 'https:' + link : link);
    if (baseURL) {
      const expected = new URL(baseURL);
      if (parsed.origin !== expected.origin) {
        // eslint-disable-next-line no-console
        console.warn(
          `Refusing to follow pagination link to unexpected origin ${parsed.origin} (expected ${expected.origin})`,
        );
        return null;
      }
    }
    return parsed.pathname + parsed.search;
  } catch {
    // eslint-disable-next-line no-console
    console.warn('Refusing to follow unparseable pagination link', link);
    return null;
  }
}

// In a real app, would likely call an error logging service.
// An API helper resolves undefined when the session has expired (handleError
// below swallows the 401; the login modal takes over). Loaders and list hooks
// turn that into this error so callers can tell it apart from a real failure
// and stay quiet instead of reporting one.
export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

export function handleError(error: any) {
  // Don't log or throw 401 errors - they're handled by the interceptor and modal
  if (error?.response?.status === 401) {
    // Silently handle 401s - the modal is already shown
    return;
  }

  // Format error message properly
  let errorMessage = 'API call failed.';
  if (error?.message) {
    errorMessage += ' ' + error.message;
  } else if (error?.response?.data) {
    if (typeof error.response.data === 'string') {
      errorMessage += ' ' + error.response.data;
    } else if (error.response.data.error) {
      errorMessage += ' ' + error.response.data.error;
    } else {
      errorMessage += ' ' + JSON.stringify(error.response.data);
    }
  } else if (typeof error === 'string') {
    errorMessage += ' ' + error;
  } else if (error?.toString && error.toString() !== '[object Object]') {
    errorMessage += ' ' + error.toString();
  }

  // eslint-disable-next-line no-console
  console.error(errorMessage, error);
  throw error;
}

export function getQueryString(query: any) {
  let queryString = '';
  let str = [];

  for (var p in query) {
    var type = typeof query[p];
    if (type === 'object') {
      var arr = query[p];
      for (var v in arr) {
        if (arr[v] !== null)
          str.push(encodeURIComponent(p) + '=' + encodeURIComponent(arr[v]));
      }
    } else {
      if (query[p] !== null)
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(query[p]));
    }
  }
  queryString = str.join('&');
  return queryString;
}

export const page_size = 20; // Pagination size for table lists (paginated, user-scrollable)

// The URL of one page of a limit/offset list, e.g. listPageUrl('/routes/', 40).
export const listPageUrl = (path: string, offset: number) =>
  process.env.REACT_APP_BASE_URL_LOCAL +
  path +
  '?pager=limit&limit=' +
  page_size +
  '&offset=' +
  offset;
export const filter_page_size = 200; // Pagination size for filter dropdowns (preloaded upfront to reduce API requests)
